// NPM Imports
import React, { Component } from "react";
import { connect } from "react-redux";
import {
  Modifier,
  RichUtils,
  EditorState,
  convertToRaw,
  getDefaultKeyBinding,
  CompositeDecorator,
} from "draft-js";
import Editor from "draft-js-plugins-editor";
import createToolbarPlugin from "draft-js-static-toolbar-plugin";
import { isEqual, throttle } from "lodash";

// Components Imports
import customBlockRenderer from "./BlockStyling";
import HTMLtoDraft from "../../utils/HTMLtoDraft/HTMLtoDraft";
import getEditorHTML from "./getEditorHtml";
import getCompositeDecorator from "./decorator";
import CustomRevvToolbar from "../CustomRevvToolbar";

// Actions Imports
import {
  updateComponentMeta,
  forceUpdateEditorSectionIdAction,
} from "../../actions/DocumentActions/index";
import EditorActions from "../../actions/EditorActions";

// Internal Imports
import { DOCUMENTS } from "../../constants";
import "draft-js-static-toolbar-plugin/lib/plugin.css";
import {
  checkSelectionHasEntity,
  previousCharEntity,
  removeEntityAndReplaceText,
  getSelectedContentState,
  updateContentStateForMultipleStyles,
  checkIfAnchorOrFocusPosHasEntity,
  setBlockType,
  getSelectedBlockTypes,
} from "../../utils/draftUtils";
import addLinkPlugin from "../RevvToolBar/plugins/addLinkPlugin";
import { sanitizeHTML } from "../../utils/documentUtils";
import {
  shouldSaveStateImmediately,
  applyStyleInEditor,
  sortContentState,
} from "../../utils/editorUtils";

// Destructuring
const { COMPONENT_FEATURE, DOCUMENT_MODE } = DOCUMENTS;
const { nukeEditorState, updateEditorState, initEditorState } = EditorActions;
const {
  FONT_SIZE_OPTIONS,
  FONT_FAMILY_OPTIONS,
  FONT_WEIGHT_OPTIONS,
} = DOCUMENTS;
const staticToolbarPlugin = createToolbarPlugin();
const plugins = [staticToolbarPlugin, addLinkPlugin];

const customKeyBinding = event => {
  const keyBinding = getDefaultKeyBinding(event);
  if (keyBinding === "undo") {
    return "handleUndo";
  }
  if (keyBinding === "redo") {
    return "handleRedo";
  }
  return keyBinding;
};

const FONT_STYLE_OPTIONS = {
  ...FONT_SIZE_OPTIONS,
  ...FONT_FAMILY_OPTIONS,
  ...FONT_WEIGHT_OPTIONS,
};

const CUSTOM_STYLE_PREFIX = "FONT";
const CUSTOM_STYLE_FONT_SIZE_PREFIX = "FONTSIZE";
const CUSTOM_STYLE_FONT_COLOR_PREFIX = "FONTCOLOR";
const CUSTOM_STYLE_FONT_FAMILY_PREFIX = "FONTFAMILY";
const CUSTOM_STYLE_FONT_WEIGHT_PREFIX = "FONTWEIGHT";

function customStyleFn(style) {
  // Getting the all styles in one single string
  const styleNames = Array.isArray(style.toJS())
    ? style.toJS().toString()
    : style.toJS();

  // Method for getting the custom style
  const applyFontStyle = styleType => {
    const styleObj = {};
    if (
      typeof styleType === "string" &&
      styleType.startsWith(CUSTOM_STYLE_PREFIX)
    ) {
      if (styleType.startsWith(CUSTOM_STYLE_FONT_SIZE_PREFIX))
        styleObj.fontSize = FONT_SIZE_OPTIONS[styleType].fontSize;
      else if (styleType.startsWith(CUSTOM_STYLE_FONT_COLOR_PREFIX))
        styleObj.color = `#${styleType.split("_")[1]}`;
      else if (styleType.startsWith(CUSTOM_STYLE_FONT_FAMILY_PREFIX))
        styleObj.fontFamily = FONT_FAMILY_OPTIONS[styleType].fontFamily;
      else if (styleType.startsWith(CUSTOM_STYLE_FONT_WEIGHT_PREFIX))
        styleObj.fontWeight = FONT_WEIGHT_OPTIONS[styleType].fontWeight;
    }
    return styleObj;
  };

  // Check whether there are custom styles present or not
  let isCustomStylePresent = false;
  const splitedStyles = styleNames ? styleNames.split(",") : [];
  if (splitedStyles && splitedStyles.length > 0) {
    const findCusStyle = splitedStyles.find(stStyle =>
      stStyle.startsWith(CUSTOM_STYLE_PREFIX),
    );
    if (findCusStyle) {
      isCustomStylePresent = true;
    }
  }

  // If custom styles are present then, get the all font styles
  let fontStyle = {};
  if (isCustomStylePresent) {
    const stylesList = styleNames.split(",");
    fontStyle = stylesList.reduce((styles, styleName) => {
      if (typeof styleName === "string") {
        styles = {
          ...styles,
          ...applyFontStyle(styleName),
        };
      } else if (Array.isArray(styleNames)) {
        styleNames.forEach(name => {
          styles = {
            ...styles,
            ...applyFontStyle(name),
          };
        });
      }
      return styles;
    }, {});
  }
  return fontStyle;
}

class CustomRevvEditor extends Component {
  state = {
    editorState: EditorState.createEmpty(getCompositeDecorator()),
  };

  compositeDecorator = getCompositeDecorator();

  // "revvThrottle" : this method must be called only once,
  // otherwise it will create the multiple execution contexts of "revvThrottle" and
  // revvThrottle will not work as expected
  _revvThrottle = throttle(
    editorState => {
      this._saveEditor(editorState);
    },
    2000,
    { trailing: true },
  );

  componentDidMount() {
    const editorState = this._generateEditorState();
    this.setState({
      editorState,
    });

    if (!this.props.readOnly) {
      document.addEventListener("copy", this._cutAndCopyEditorContent, false);
      document.addEventListener("cut", this._cutAndCopyEditorContent, false);
    }
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.isComponentActive &&
      this.props.preventEditorFocusOnDrag &&
      (this.props.isHoveredComponentActive || !this.props.isDocCustomTextEditor)
    ) {
      this._focus();
    }

    // Initiate the Editor state( in case of new text or table block addition)
    if (
      !this.props.readOnly &&
      (this.props.isDocCustomTextEditor ||
        this.props.isCustomTableTextEditor) &&
      this.props.isComponentActive &&
      !this.props.clickedEditorState &&
      !this.props.clickedSection
    ) {
      this._initEditorState();
    }

    if (this.props.forceUpdateEditorSectionId === this.props.section.id) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(
        {
          editorState: this._generateEditorState(),
        },
        () => {
          this.props.forceUpdateEditorSectionIdAction(null);
        },
      );
    }

    // Highlight for metalens
    if (
      (this.props.metalensIsActive &&
        prevProps.metadataReRenderId !== this.props.metadataReRenderId) ||
      prevProps.spotlightEntitiesActive !==
        this.props.spotlightEntitiesActive ||
      prevProps.quickReviewActive !== this.props.quickReviewActive
    ) {
      // do update here
      this._updateMetalensData();
    }

    // Need to lock the editor's content editable behaviour in case of readOnly mode
    if (
      this.editEditor &&
      this.editEditor.editor &&
      this.editEditor.editor.editor
    ) {
      const element = this.editEditor.editor.editor;
      const firstChildEle = element.firstChild;
      if (
        firstChildEle &&
        firstChildEle.childNodes &&
        firstChildEle.childNodes.length > 0
      ) {
        firstChildEle.childNodes.forEach(ele => {
          ele.contentEditable = !this.props.readOnly;
        });
      }
    }

    // when component is updated from toolbar
    if (
      this.props.clickedSection &&
      this.props.clickedSection.id === this.props.section.id &&
      this.props.isComponentActive
    ) {
      this._saveEditorTrigger(this.props.clickedEditorState);
    }
  }

  componentWillUnmount() {
    if (!this.props.readOnly) {
      document.removeEventListener(
        "copy",
        this._cutAndCopyEditorContent,
        false,
      );
      document.removeEventListener("cut", this._cutAndCopyEditorContent, false);
    }
  }

  _updateMetalensData = () => {
    this.setState({
      editorState: this._generateEditorState(),
    });
  };

  _generateEditorState = () => {
    let { editorState } = EditorState.createEmpty(this.compositeDecorator);
    const { mode, section } = this.props;
    const { data, currentContent } = section;
    // For Document case
    if (COMPONENT_FEATURE[mode].allowDiff) {
      if (currentContent && Object.keys(currentContent).length !== 0) {
        editorState = EditorState.createWithContent(
          currentContent,
          this.compositeDecorator,
        );
      }
    }
    // For Template case
    else if (data && Object.keys(data).length !== 0) {
      editorState = EditorState.createWithContent(
        data,
        this.compositeDecorator,
      );
    }
    return editorState;
  };

  _cutAndCopyEditorContent = event => {
    if (
      this.props.isComponentActive &&
      window.getSelection() &&
      window.getSelection().toString()
    ) {
      event.preventDefault();
      if (event && (event.type === "copy" || event.type === "cut")) {
        const editorState = this._getEditorState();
        const selectedContent = getSelectedContentState(editorState);
        const html = getEditorHTML(selectedContent);
        event.clipboardData.setData(
          "text/plain",
          window.getSelection().toString(),
        );
        event.clipboardData.setData("text/html", html);
      }
    }
  };

  /** ::: "Prevent_The_Click_Event_On_Active_Editor" :::
   * it will prevent the active editor to deactivate,
   * in a case of adding a new text component and then click on editor
   */
  // _handleClickEventListner = isAddEvent => {
  //   const { isComponentActive, isDocCustomTextEditor } = this.props;
  //   if (isDocCustomTextEditor && isComponentActive) {
  //     const { section } = this.props;
  //     const id = `edit_editor_id_${section ? section.id : null}`;
  //     const currentEditorElement = document.getElementById(id);
  //     if (currentEditorElement) {
  //       if (isAddEvent) {
  //         currentEditorElement.addEventListener(
  //           "click",
  //           this._preventClickInsideEditor,
  //           false,
  //         );
  //       } else {
  //         currentEditorElement.removeEventListener(
  //           "click",
  //           this._preventClickInsideEditor,
  //           false,
  //         );
  //       }
  //     }
  //   }
  // };

  _preventClickInsideEditor = event => {
    event.preventDefault();
    event.stopPropagation();
  };

  _focus = () => {
    if (!this.props.readOnly && this.editEditor) {
      if (this.props.customColorDropdownStatus) {
        const editorState = this._getEditorState();
        if (!checkIfAnchorOrFocusPosHasEntity(editorState, "DYNAMICFIELD")) {
          this.editEditor.focus();
        }
      } else {
        this.editEditor.focus();
      }
    }
  };

  _initEditorState = () => {
    this.props.initEditorState(this.state.editorState, this.props.section);
    this.props.updateComponentMeta(this.props.componentMeta);
  };

  /** ::: "Handle_Editor_On_Change" :::
   * If there is an acitve editor and active component then, update the Redux Wala "editorState",
   * else update the local wala "editorState
   */
  _updateEditorState = editorState => {
    if (
      this.props.isComponentActive &&
      this.props.clickedSection &&
      this.props.clickedSection.id === this.props.section.id
    ) {
      this.props.updateEditorState(editorState);
      this._saveEditorTrigger(editorState);
      // this._revvThrottle(editorState);
      this.setState({ editorState });
    } else {
      this.setState({ editorState });
    }
  };

  _updateLocalEditorState = editorState => {
    this.setState({ editorState });
  };

  /** ::: "Get_The_Correct_Editor_State" :::
   * If there is an acitve editor then, get the Redux Wala "editorState",
   * else get the local wala "editorState
   */
  _getEditorState = () => {
    const {
      clickedSection,
      clickedEditorState,
      section,
      isComponentActive,
    } = this.props;
    if (
      clickedSection &&
      clickedSection.id === section.id &&
      isComponentActive
    ) {
      return clickedEditorState;
    }
    return this.state.editorState;
  };

  _handleChange = newEditorState => {
    const { section, readOnly } = this.props;
    if (section.lock || readOnly) {
      return;
    }
    // Here adding the conditions to prevent the change in case of "form fields"
    const editorState = this._getEditorState();
    const oldContentState = editorState.getCurrentContent();
    const newContentState = newEditorState.getCurrentContent();
    if (
      oldContentState !== newContentState &&
      checkSelectionHasEntity(newEditorState, "DYNAMICFIELD")
    ) {
      return;
    }

    // Normal flow of edior onChange
    if (newEditorState && this.props.preventEditorFocusOnDrag) {
      this._updateEditorState(newEditorState);
    }

    const change = !isEqual(
      convertToRaw(oldContentState),
      convertToRaw(newContentState),
    );

    // To handle color picker dropdown and save cell new data
    if (change)
      // eslint-disable-next-line no-unused-expressions
      this.props.handleColorPicker ? this.props.handleColorPicker() : null;
  };

  _handleChangeOnBackspace = newEditorState => {
    const { section, readOnly } = this.props;
    if (section.lock || readOnly) {
      return;
    }
    if (newEditorState) {
      this._updateEditorState(newEditorState);
    }
  };

  _handleRemoveRange = () => {
    const editorState = this._getEditorState();
    const contentState = editorState.getCurrentContent();
    const selectionState = editorState.getSelection();
    const direction = selectionState.isBackward ? "backward" : "forward";
    const anchorOffset = selectionState.getAnchorOffset();
    const focusOffset = selectionState.getFocusOffset();
    if (anchorOffset === focusOffset) {
      return false;
    }
    const textWithoutRange = Modifier.removeRange(
      contentState,
      selectionState,
      direction,
    );
    this._updateEditorState(
      EditorState.push(editorState, textWithoutRange, "remove-range"),
    );
    return true;
  };

  _saveEditor = editorState => {
    const { section, handleBlur, mode, highlightedContent } = this.props;

    let change = this.isEditorDataChanged(editorState);
    const editorTextValue = editorState
      .getCurrentContent()
      .getPlainText("\u0001");
    if (
      editorTextValue === "" &&
      this.props.metaData &&
      this.props.metaData.BLOCKTYPE
    ) {
      change = false;
    }

    let newSection = {
      ...section,
    };
    // if undo is allowed, then save undo and redo stack
    if (COMPONENT_FEATURE[mode].allowUndo) {
      newSection = {
        ...newSection,
      };
    }

    // if change is there, update diffState, current content, data and html
    if (change) {
      if (COMPONENT_FEATURE[mode].allowDiff) {
        newSection = {
          ...newSection,
          currentContent: editorState.getCurrentContent(),
        };
      } else {
        newSection = {
          ...newSection,
          data: editorState.getCurrentContent(),
        };
      }
      // get header of the current content
      const firstBlock = editorState.getCurrentContent().getFirstBlock();
      newSection.header = firstBlock.getText().trim();
      if (firstBlock.getType().startsWith("header")) {
        newSection.label = firstBlock.getText().trim();
      } else {
        newSection.label = newSection.header;
      }
    }

    // check if there is any highlight
    if (highlightedContent) {
      change = change || null;
      if (COMPONENT_FEATURE[mode].allowDiff) {
        newSection = {
          ...newSection,
          highlightedContent,
        };
      } else {
        newSection = {
          ...newSection,
          highlightedContent,
        };
      }
    }

    if (this.props.isSeparateToolbar && change) {
      handleBlur(newSection, change, false, { ...section });
    } else if (!this.props.isSeparateToolbar) {
      // If it is not a "isSeparateToolbar" case means if it is a case of clause option editor or form mode text area editor,
      // then have to call the handle blur to loose the focus of text editor
      handleBlur(newSection, change, false, { ...section });
    }
  };

  _handleBlur = () => {
    /**
     * If it is a case of Separate Toolbar then, do the following actions,
     * 1. Update the local "EditorState" to redux wala "EditorState" to reflect the latest changes
     * 2. Nuke the Redux wala EditorState
     * 3. Deactivate the current clicked component in case of "Custom Text"
     */
    if (this.props.isSeparateToolbar) {
      const editorState = this._getEditorState();
      this.setState({ editorState }, () => {
        if (this.props.preventClickedComponentIdUpdate) {
          if (this.props.clickedSection || this.props.clickedEditorState) {
            this.props.nukeEditorState();
          }
          if (
            this.props.isComponentActive &&
            this.props.updateDocumentClickedComponentID
          ) {
            this.props.updateDocumentClickedComponentID(null, true, null);
          }
          // Calling editor's blur method to loose the editor's focus
          // && this.isEditorFocused
          if (this.editEditor) {
            this.editEditor.blur();
          }
        }
      });
    }
  };

  _handleOnTab = e => {
    const editorState = this._getEditorState();
    const maxDepth = 4;
    const updatedEditorState = RichUtils.onTab(e, editorState, maxDepth);
    this._handleChange(updatedEditorState);
  };

  _removeEntity = (editorState, prevCharEntity) => {
    const currentContent = editorState.getCurrentContent();
    const {
      currentContent: newContent,
      selectionState,
    } = removeEntityAndReplaceText(
      currentContent,
      prevCharEntity.entity.data.id,
      "",
      true,
      editorState,
      prevCharEntity,
    );
    const newEditorState = EditorState.set(editorState, {
      currentContent: newContent,
    });
    const updatedEditorSelection = EditorState.acceptSelection(
      newEditorState,
      selectionState,
    );
    return updatedEditorSelection;
  };

  _handleKeyCommand = command => {
    const editorState = this._getEditorState();
    if (command === "split-block") {
      const selectionState = editorState.getSelection();
      // Check if both prev and next keys have "DYNAMICFIELD" entity
      if (checkSelectionHasEntity(editorState, "DYNAMICFIELD")) {
        return "handled";
      }
      // Check if the prev key has "DYNAMICFIELD" entity
      const currentContent = editorState.getCurrentContent();
      const anchorKey = selectionState.getAnchorKey();
      const block = currentContent.getBlockForKey(anchorKey);
      const position = selectionState.getAnchorOffset();
      const prevEntityID = block.getEntityAt(position - 1);
      if (prevEntityID) {
        const entity = currentContent.getEntity(prevEntityID);
        if (entity.type === "DYNAMICFIELD") {
          const newEditorState = RichUtils.insertSoftNewline(editorState);
          if (newEditorState && newEditorState !== editorState) {
            this._handleChange(newEditorState);
          }
          return "handled";
        }
      }
    }
    if (command === "handleUndo") {
      this._handleBlur();
      return "handled";
    }
    if (command === "handleRedo") {
      this._handleBlur();
      return "handled";
    }
    if (command === "backspace") {
      const prevCharEntity = previousCharEntity(editorState);
      if (prevCharEntity) {
        const newEditorState = this._removeEntity(editorState, prevCharEntity);
        this._handleChangeOnBackspace(newEditorState);
        return "handled";
      }
      if (!prevCharEntity) {
        const removed = this._handleRemoveRange();
        if (!removed) {
          return "not-handled";
        }
        return "handled";
      }
      return "not-handled";
    }

    if (command === "bold") {
      const blockTypes = getSelectedBlockTypes(editorState);
      const headerType =
        blockTypes && blockTypes.length > 0
          ? blockTypes.find(type => type && type.includes("header"))
          : null;
      if (headerType) {
        const newEditorState = setBlockType("unstyled", editorState);
        if (newEditorState && newEditorState !== editorState) {
          this._handleChange(newEditorState);
        }
        return "handled";
      }
    }

    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState !== undefined && newState !== null) {
      this._handleChange(newState);
      return "handled";
    }
    return "not-handled";
  };

  _handlePastedText = (text, textHtml) => {
    const editorState = this._getEditorState();

    // Don't allow paste if the selection has "form fields"
    if (checkSelectionHasEntity(editorState, "DYNAMICFIELD")) {
      return "handled";
    }

    // if "text" and "textHtml" both are "null" or "undefined" then, stop pasting
    if (!text && !textHtml) return "handled";

    // ::::: AUTO_CORRECTION_CASE :::::
    // If there is only "text" and "textHtml" is undefiend, then it is a case of auto correction,
    // in this case, we'll have to update the text along with current inline styles
    if (text && !textHtml) {
      const selectionState = editorState.getSelection();
      const currentContent = editorState.getCurrentContent();
      const currentFocus = editorState.getSelection().getFocusKey();
      const inlineStyle = editorState.getCurrentInlineStyle(currentFocus);

      // replace entity text with actual text and apply no entity
      const newContentState = Modifier.replaceText(
        currentContent,
        selectionState,
        text,
        inlineStyle,
        null,
      );

      const newEditorState = EditorState.push(
        editorState,
        newContentState,
        "replace-text",
      );

      this._handleChange(newEditorState);
      return "handled";
    }
    const cleanText = sanitizeHTML(textHtml || text);
    const contentState = HTMLtoDraft(cleanText, DOCUMENT_MODE.PREVIEW);
    const updatedContentState = updateContentStateForMultipleStyles(
      contentState,
    );
    // --- SINGLE_BLOCK_OF_TYPE_*_CASE ---
    // Here, if the pasted content("updatedContentState") contains only one block,
    // then, in this case "replaceWithFragment" method removes the block type and makes it "unstyled"
    // So, in this case, updating that block type for updated editor state
    const blocks = updatedContentState.getBlocksAsArray();
    let singleBlockPastedTextType = null;
    if (blocks && blocks.length === 1) {
      const block = blocks[0];
      const blockType = block.getType();
      if (
        blockType.includes("header") ||
        blockType.includes("unordered-list") ||
        blockType.includes("ordered-list")
      ) {
        singleBlockPastedTextType = blockType;
      }
    }

    const newContent = Modifier.replaceWithFragment(
      editorState.getCurrentContent(),
      editorState.getSelection(),
      updatedContentState.blockMap,
    );

    let newEditorState = EditorState.push(
      editorState,
      newContent,
      "insert-fragment",
    );
    // --- --- SINGLE_BLOCK_OF_TYPE_*_CASE --- ---
    if (singleBlockPastedTextType) {
      const newContentForSingleBloc = Modifier.setBlockType(
        newContent,
        newEditorState.getSelection(),
        singleBlockPastedTextType,
      );
      newEditorState = EditorState.set(newEditorState, {
        currentContent: newContentForSingleBloc,
      });
    }

    this._handleChange(newEditorState);
    return "handled";
  };

  /** ::: "Handle_Editor_Click" :::
   * This method is for initiaing the editor state with current clicked section
   */
  _handleEditEditorClick = () => {
    if (
      !this.props.readOnly &&
      (this.props.isDocCustomTextEditor || this.props.isCustomTableTextEditor)
    ) {
      let checkCustomTextCondition = null;
      if (!this.props.clickedEditorState && !this.props.clickedSection) {
        // in case of normal custom text
        if (this.props.isDocCustomTextEditor) {
          checkCustomTextCondition = !this.props.isComponentActive;
        }
        // in case of Table custom text
        else if (this.props.isCustomTableTextEditor) {
          checkCustomTextCondition = true;
        }
      }
      if (checkCustomTextCondition) {
        this._initEditorState();
      }
    }
  };

  _handleEditorOnBlur = () => {
    if (!this.props.readOnly) {
      if (this.props.isSeparateToolbar) {
        if (
          this.props.isDocCustomTextEditor &&
          (this.props.clickedSection || this.props.clickedEditorState)
        ) {
          this._handleBlur();
        } else if (
          this.props.isCustomTableTextEditor &&
          this.props.isComponentActive
        ) {
          this._handleBlur();
          this.props.handleDropdownsOnBlur();
        }
      } else {
        // If it is not a "isSeparateToolbar" case means if it is a case of
        // (clause option editor or form mode text area editor), have to call only save method
        // Or in case of on blur cancel all throttled calls & save immediately
        const editorState = this._getEditorState();
        this._saveEditorTrigger(editorState, true);
      }
    }
  };

  // if decorator is missing or incorrect then apply it
  _fixEditorDecorator = editorState => {
    if (
      editorState &&
      editorState instanceof EditorState &&
      !(editorState.getDecorator() instanceof CompositeDecorator)
    ) {
      return EditorState.set(editorState, {
        decorator: this.compositeDecorator,
      });
    }

    return editorState;
  };

  /**
   * Here, getting the correct editor placeholder,
   * 1. "Start typing..." ---> if the editor has no text or empty editor's
   *                           first block type is other than "unordered-list" or "ordered-list".
   * 2. "" otherwise
   */
  _getEditorPlaceholder = editorState => {
    let placeholder = "";
    if (
      this.props.isComponentActive &&
      !editorState.getCurrentContent().hasText()
    ) {
      const blocks = editorState.getCurrentContent().getBlocksAsArray();
      if (blocks && blocks.length === 1) {
        const block = blocks[0];
        const blockType = block.getType();
        if (
          !(
            blockType.includes("unordered-list") ||
            blockType.includes("ordered-list")
          )
        ) {
          placeholder = "Start typing...";
        }
      }
    }
    return placeholder;
  };

  _getStyling = editorState => {
    const { metaData } = this.props;
    const selection = editorState.getSelection();
    const anchorOffset = selection.getAnchorOffset();
    const focusOffset = selection.getFocusOffset();
    const editorTextValue = editorState
      .getCurrentContent()
      .getPlainText("\u0001");
    const listStylesToCheck = [
      "FONTSIZE",
      "FONTCOLOR",
      "FONTFAMILY",
      "FONTWEIGHT",
      "BOLD",
      "ITALIC",
      "UNDERLINE",
      "BLOCKTYPE",
    ];
    if (
      metaData &&
      selection.isCollapsed() &&
      anchorOffset === 0 &&
      focusOffset === 0 &&
      editorTextValue === ""
    ) {
      listStylesToCheck.forEach(style => {
        editorState = applyStyleInEditor(editorState, metaData, style);
      });
    }
    return editorState;
  };

  _saveEditorTrigger(editorState, forceImmediate) {
    const immediate = shouldSaveStateImmediately(editorState);
    if (immediate || forceImmediate) {
      this._revvThrottle.cancel(); // cancel throttled calls
      this._saveEditor(editorState); // call immediately
    } else {
      this._revvThrottle(editorState); // schedule time based throttle
    }
  }

  isEditorDataChanged(newEditorState) {
    const oldCurrentContent = COMPONENT_FEATURE[this.props.mode].allowDiff
      ? this.props.section.currentContent
      : this.props.section.data;

    // if the state is changed then no need to check deep equal
    if (newEditorState.getCurrentContent() !== oldCurrentContent) {
      return true;
    }

    // when state doesn't change but styling, DF etc is changed deep check is required
    let oldCurrentContentRaw = convertToRaw(oldCurrentContent);
    let newCurrentContent;
    if (newEditorState) {
      newCurrentContent = convertToRaw(newEditorState.getCurrentContent());
    }

    // sorting arrays to make sure isEqual works for arrays props as well
    oldCurrentContentRaw = sortContentState(oldCurrentContentRaw);
    newCurrentContent = sortContentState(newCurrentContent);

    const change = !isEqual(oldCurrentContentRaw, newCurrentContent);
    return change;
  }

  render() {
    let editorState =
      this.props.showForm && this.props.mode === "DOCUMENT"
        ? this._generateEditorState()
        : this._getEditorState();

    // when editor state is updated from multiple places it becomes null in bertween renders
    if (!editorState) return null;

    editorState = this._fixEditorDecorator(editorState);
    if (
      editorState.getSelection().isCollapsed() &&
      editorState.getSelection().getAnchorOffset() === 0 &&
      editorState.getCurrentInlineStyle().size === 0 &&
      this.props.isComponentActive
    )
      editorState = this._getStyling(editorState);
    const { isComponentActive, section, isSeparateToolbar, mode } = this.props;
    return (
      <div
        id={`edit_editor_id_${section.id}`}
        className={`editor ${isComponentActive ? "active-editor" : ""} ${
          this.props.readOnly ? "locked-editor" : ""
        }`}
        onClick={this._handleEditEditorClick}
        onKeyPress={() => {}}
        onBlur={this._handleEditorOnBlur}
        role="button"
        tabIndex="0">
        {/**
         * ::===:: CustomRevvToolbar ::===::
         * It is being used here only for the scenarios where
         * Editor and Toolbar come together(ex: add clause option's Text Editor and
         * form mode text_area Text Editor)
         */}
        {!this.props.readOnly && isComponentActive && !isSeparateToolbar && (
          <CustomRevvToolbar
            mode={mode}
            section={section}
            updateLocalEditorState={this._updateLocalEditorState}
            customToolbarRef={this.props.customToolbarRef}
            isSeparateToolbar={false}
            localEditorState={this.state.editorState}
          />
        )}
        {editorState && editorState instanceof EditorState && (
          <Editor
            section={section}
            customStyleMap={FONT_STYLE_OPTIONS}
            customStyleFn={customStyleFn}
            editorState={editorState}
            placeholder={this._getEditorPlaceholder(editorState)}
            // onBlur={this._handleEditorOnBlur}
            onChange={this._handleChange}
            handleKeyCommand={this._handleKeyCommand}
            handlePastedText={this._handlePastedText}
            keyBindingFn={customKeyBinding}
            blockRendererFn={customBlockRenderer(
              this._getEditorState,
              this._updateEditorState,
              this.props.readOnly,
            )}
            onTab={this._handleOnTab}
            plugins={plugins}
            ref={element => {
              this.editEditor = element;
            }}
            readOnly={this.props.readOnly}
            data-test="customRevvEditor"
            handleDrop={() => "handled"}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const {
    clickedSection,
    clickedEditorState,
    highlightedContent,
  } = state.EditorStore;
  const {
    document: { showForm, id: docID, meta_data: docMetaData } = {},
    forceUpdateEditorSectionId,
    preventClickedComponentIdUpdate,
    customColorDropdownStatus,
    preventEditorFocusOnDrag,
  } = state.DocumentStore;
  const {
    active,
    metadataReRenderId,
    spotlightEntitiesActive,
    quickReviewActive,
  } = state.MetalensStore;
  const {
    template: { meta_data: tempMetaData },
  } = state.TemplateStore;

  let metaData;

  if (ownProps.mode === "DOCUMENT") {
    metaData = docMetaData;
  }

  if (ownProps.mode === "TEMPLATE") {
    metaData = tempMetaData;
  }

  let returnPayload = {
    highlightedContent,
    showForm,
    metalensIsActive: active,
    metadataReRenderId,
    forceUpdateEditorSectionId,
    docID,
    spotlightEntitiesActive,
    customColorDropdownStatus,
    preventClickedComponentIdUpdate,
    preventEditorFocusOnDrag,
    quickReviewActive,
    metaData,
  };

  if (clickedSection && clickedSection.id === ownProps.section.id) {
    returnPayload = { ...returnPayload, clickedSection, clickedEditorState };
  }
  return returnPayload;
};

const mapDispatchToProps = {
  nukeEditorState,
  updateEditorState,
  initEditorState,
  updateComponentMeta,
  forceUpdateEditorSectionIdAction,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CustomRevvEditor);



// WEBPACK FOOTER //
// ./src/components/CustomRevvEditor/index.js